export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    public details?: unknown,
  ) {
    super(code);
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method,
    credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  const payload = text ? JSON.parse(text) : {};
  if (!res.ok) {
    throw new ApiError(res.status, payload.error || `http_${res.status}`, payload.details);
  }
  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  delete: <T>(path: string, body?: unknown) => request<T>("DELETE", path, body),
};

export interface Mailbox {
  address: string;
  domain: string;
  maildir: string;
  quota_bytes: string;
  active: boolean;
  forward_to: string | null;
  forward_keep_copy: boolean;
  created_at: string;
  updated_at: string;
}

export interface MailboxUsage {
  ok: true;
  bytes: number | null;
  limitBytes: number | null;
  percent: number | null;
}

export interface Alias {
  address: string;
  goto: string;
  domain: string;
  active: boolean;
  created_at: string;
}

export interface Domain {
  name: string;
  active: boolean;
  created_at: string;
  mailbox_count: string;
  alias_count: string;
}

export interface AuditEntry {
  id: string;
  admin_email: string;
  action: string;
  target: string | null;
  ip: string | null;
  details: Record<string, unknown>;
  at: string;
}

export interface DkimKey {
  domain: string;
  selector: string;
  createdAt: number | null;
  txt: string | null;
  known: boolean;
  recordName: string;
}

export type DnsCheckStatus = "pass" | "warn" | "fail" | "info";
export interface DnsCheck {
  key: string;
  label: string;
  status: DnsCheckStatus;
  actual: string | null;
  expected: string | null;
  note?: string;
}
export interface DnsReport {
  domain: string;
  checkedAt: string;
  checks: DnsCheck[];
}

export interface SieveScript {
  name: string;
  active: boolean;
}

export interface QueueEntry {
  queue_name?: string;
  queue_id: string;
  arrival_time?: number;
  message_size?: number;
  forced_expire?: boolean;
  sender?: string;
  recipients?: { address: string; delay_reason?: string }[];
}

export interface DashboardCounters {
  mailboxes: { active: number; disabled: number; total: number };
  aliases: number;
  domains: number;
  auditToday: number;
}

export interface DashboardStorage {
  totalQuotaBytes: number;
  totalUsedBytes: number;
  topByPercent: {
    address: string;
    usedBytes: number;
    quotaBytes: number;
    percent: number | null;
  }[];
}

export interface DashboardQueueSummary {
  total: number;
  active: number;
  deferred: number;
  held: number;
  oldestAgeSeconds: number;
}

export interface DashboardRecentAudit {
  entries: {
    id: string;
    admin_email: string;
    action: string;
    target: string | null;
    at: string;
  }[];
}

export interface DashboardSystem {
  services: { name: string; active: boolean; state: string }[];
  cert:
    | { ok: true; notAfter: string; daysLeft: number }
    | { error: string };
  rspamd: {
    version?: string;
    uptime?: number;
    scanned: number;
    learned: number;
    spam_count: number;
    ham_count: number;
    connections: number;
  } | null;
}
